import { useEffect, useCallback, useMemo, useState, useRef } from 'react'
import { TbLoader2 } from 'react-icons/tb'
import { useDispatch, useSelector } from 'react-redux'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Icon } from '@iconify/react'
import { Empty, Input, Row, Col } from 'antd'
import { debounce } from 'lodash'

import {
    jobState,
    changePage,
    getListJobs,
    searchJob,
    switchJobModal,
} from './store/job.slice'
import HeaderPrimaryButton from '../../components/HeaderPrimaryButton/HeaderPrimaryButton'
import JobCards from './components/JobCards'
import AddEditJob from './components/AddEditJob'
import { TalentToolbar } from '../talent/TalentToolbar'
import { loginState } from '../login/login.slice'
import { getTalentAccess } from '../../helpers/reportsPermission'
import styles from './jobs.module.css'

function Jobs() {
    const dispatch = useDispatch()
    const fetchingMoreRef = useRef(false)
    const { userProfile } = useSelector(loginState)
    const talentAccess = useMemo(
        () => getTalentAccess(userProfile),
        [userProfile]
    )

    const {
        modalVariations,
        pageLimit,
        isNext,
        status,
        jobList,
        filter,
        search,
    } = useSelector(jobState)

    const [searchValue, setSearchValue] = useState(search || '')

    const loadJobs = useCallback(
        ({ skip = 0, searchTerm = search || '' } = {}) => {
            dispatch(
                changePage({
                    currentPage: Math.floor(skip / pageLimit) + 1,
                    skip,
                    pageLimit,
                })
            )
            return dispatch(
                getListJobs({
                    limit: pageLimit,
                    skip,
                    search: searchTerm,
                    filter: { ...(filter || {}) },
                })
            )
        },
        [dispatch, pageLimit, filter, search]
    )

    useEffect(() => {
        loadJobs({ skip: 0, searchTerm: '' })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchMoreJobs = useCallback(() => {
        if (!isNext || status === 'loading' || status === 'loadingMore') {
            return
        }
        if (fetchingMoreRef.current) {
            return
        }

        fetchingMoreRef.current = true
        const nextSkip = (jobList?.length || 0)

        loadJobs({ skip: nextSkip, searchTerm: search || '' }).finally(() => {
            fetchingMoreRef.current = false
        })
    }, [isNext, status, jobList?.length, loadJobs, search])

    const debouncedSearch = useMemo(
        () =>
            debounce((value) => {
                dispatch(searchJob(value))
                loadJobs({ skip: 0, searchTerm: value })
            }, 350),
        [dispatch, loadJobs]
    )

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

    const handleSearchChange = (event) => {
        const value = event.target.value
        setSearchValue(value)
        debouncedSearch(value)
    }

    const isInitialLoading = status === 'loading' && (!jobList || jobList.length === 0)

    return (
        <div className={styles.page}>
            <TalentToolbar>
                <div className={styles.tabToolbarInline}>
                    <div className={styles.controls}>
                        <div className={styles.searchWrap}>
                            <Input
                                allowClear
                                size="large"
                                placeholder="Search job"
                                className={styles.search}
                                prefix={
                                    <Icon
                                        icon="mdi:magnify"
                                        width={18}
                                        height={18}
                                    />
                                }
                                value={searchValue}
                                onChange={handleSearchChange}
                            />
                        </div>
                        {talentAccess.canEdit ? (
                            <HeaderPrimaryButton
                                onClick={() => {
                                    dispatch(
                                        switchJobModal({
                                            ...modalVariations,
                                            open: true,
                                            isEdit: false,
                                            selectedJobID: null,
                                        })
                                    )
                                }}
                            >
                                Add Job
                            </HeaderPrimaryButton>
                        ) : null}
                    </div>
                </div>
            </TalentToolbar>

            <div id="jobs-scroll-container" className={styles.content}>
                {isInitialLoading ? (
                    <div className={styles.centeredState}>
                        <TbLoader2
                            size={36}
                            className="spin-loader-resume"
                            color="#4f46e5"
                        />
                    </div>
                ) : (
                    <InfiniteScroll
                        dataLength={jobList?.length || 0}
                        next={fetchMoreJobs}
                        hasMore={Boolean(isNext)}
                        scrollableTarget="jobs-scroll-container"
                        loader={
                            status === 'loadingMore' ? (
                                <div className={styles.loaderRow}>
                                    <TbLoader2
                                        size={24}
                                        className="spin-loader-resume"
                                        color="#4f46e5"
                                    />
                                </div>
                            ) : null
                        }
                        endMessage={null}
                        style={{ overflow: 'inherit' }}
                    >
                        {jobList?.length > 0 ? (
                            <Row
                                gutter={[20, 20]}
                                className={styles.jobsGrid}
                                style={{ paddingTop: 5 }}
                            >
                                {jobList.map((job, index) => (
                                    <Col
                                        key={job._id || index}
                                        xs={24}
                                        sm={12}
                                        lg={8}
                                        xl={6}
                                    >
                                        <JobCards job={job} />
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                            <div className={styles.centeredState}>
                                <Empty description="No jobs found" />
                            </div>
                        )}
                    </InfiniteScroll>
                )}
            </div>

            {talentAccess.canEdit ? <AddEditJob /> : null}
        </div>
    )
}

export default Jobs
